import type { ReactiveController, ReactiveControllerHost } from 'lit'

/** Options for configuring `RovingTabindexController`. */
export interface RovingTabindexOptions {
  /**
   * CSS selector used to query focusable items inside the host's shadow root
   * (or the host itself if no shadow root is present).
   */
  selector: string
  /**
   * Number of columns in the grid layout. Use `1` for a single-column list.
   * Controls how many positions `ArrowDown`/`ArrowUp` move.
   */
  columns: number
}

/**
 * A Lit `ReactiveController` that manages keyboard-accessible roving tabindex
 * for grid and list views.
 *
 * Supported keys:
 * - `ArrowRight` / `ArrowLeft` — move by 1 position (wraps at boundaries)
 * - `ArrowDown` / `ArrowUp` — move by `columns` positions (wraps at boundaries)
 * - `Home` — jump to first item
 * - `End` — jump to last item
 *
 * `Space` and `Enter` are intentionally NOT handled; host components should
 * react to those keys themselves using `activeIndex`.
 *
 * @example
 * ```ts
 * class MyGrid extends LitElement {
 *   private _roving = new RovingTabindexController(this, {
 *     selector: '.tile',
 *     columns: 4,
 *   })
 * }
 * ```
 */
export class RovingTabindexController implements ReactiveController {
  /** The current 0-based index of the focused item. */
  activeIndex = 0

  private readonly _host: ReactiveControllerHost & EventTarget
  private readonly _options: RovingTabindexOptions
  private _items: Element[] = []
  private readonly _handleKeydown: (event: KeyboardEvent) => void

  constructor(host: ReactiveControllerHost, options: RovingTabindexOptions) {
    this._host = host as ReactiveControllerHost & EventTarget
    this._options = options
    this._handleKeydown = this._onKeydown.bind(this)
    host.addController(this)
  }

  hostConnected(): void {
    this._host.addEventListener('keydown', this._handleKeydown as EventListener)
  }

  hostDisconnected(): void {
    this._host.removeEventListener('keydown', this._handleKeydown as EventListener)
  }

  hostUpdated(): void {
    const root =
      (this._host as unknown as { shadowRoot: ShadowRoot | null }).shadowRoot ?? (this._host as unknown as ParentNode)
    this._items = Array.from(root.querySelectorAll(this._options.selector))
    this._applyTabindexes()
  }

  /**
   * Moves focus to the item at the given index, updating tabindexes and
   * calling `focus()` on the newly active element.
   *
   * @param index - The 0-based index of the item to activate.
   */
  setActiveIndex(index: number): void {
    const count = this._items.length
    if (count === 0) {
      return
    }

    const next = ((index % count) + count) % count
    this.activeIndex = next
    this._applyTabindexes()

    const el = this._items[next] as HTMLElement | undefined
    el?.focus()
  }

  private _applyTabindexes(): void {
    this._items.forEach((item, i) => {
      ;(item as HTMLElement).tabIndex = i === this.activeIndex ? 0 : -1
    })
  }

  private _onKeydown(event: KeyboardEvent): void {
    const count = this._items.length
    if (count === 0) {
      return
    }
    const current = this.activeIndex
    const { columns } = this._options

    let next: number | null = null

    switch (event.key) {
      case 'ArrowRight':
        next = (current + 1) % count
        break
      case 'ArrowLeft':
        next = (current - 1 + count) % count
        break
      case 'ArrowDown': {
        const candidate = current + columns
        next = candidate < count ? candidate : 0
        break
      }
      case 'ArrowUp': {
        const candidate = current - columns
        next = candidate >= 0 ? candidate : count - 1
        break
      }
      case 'Home':
        next = 0
        break
      case 'End':
        next = count - 1
        break
      default:
        return
    }

    if (next !== null) {
      event.preventDefault()
      this.setActiveIndex(next)
    }
  }
}
